/**
 * Lista DIDs disponíveis na BRDID (GET buscar_numeros_by_area_local).
 * 1) Se existir VITE_SERVER_URL → usa o proxy do servidor WappDesk (/api/brdid/dids).
 * 2) Senão, se existir VITE_BRDID_API_TOKEN → chama a API BRDID direto (mesmo contrato do wappdesk-platform/src/utils/brdidApi.js).
 *
 * Atenção: token no front (VITE_*) fica exposto no bundle; em produção prefira o proxy.
 */

const BRDID_BASE = 'https://brdid.com.br/api'
const DEFAULT_LIST_PATH = 'buscar_numeros_by_area_local'
const DEFAULT_LOCALIDADES_PATH = 'buscar_localidades'

function parseDidsPayload(raw, res) {
  const trimmed = (raw || '').trim()
  if (!res.ok) {
    throw new Error(`Erro BRDID (${res.status}): ${trimmed.slice(0, 200) || 'falha ao listar DIDs'}`)
  }
  if (!trimmed) {
    throw new Error('Resposta da BRDID vazia.')
  }
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[')
  if (!looksLikeJson) {
    throw new Error(`Resposta inesperada: ${trimmed.slice(0, 120)}…`)
  }
  let data
  try {
    data = JSON.parse(raw)
  } catch {
    throw new Error('Resposta BRDID não é JSON válido.')
  }
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  return []
}

async function fetchViaServerProxy(areaLocal) {
  const serverUrl = (import.meta.env.VITE_SERVER_URL || '').trim().replace(/\/$/, '')
  if (!serverUrl) return null
  const url = new URL(`${serverUrl}/api/brdid/dids`)
  url.searchParams.set('area_local', String(areaLocal || '31000').trim())
  const res = await fetch(url.toString())
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json?.ok === false) {
    throw new Error(json?.error || 'Erro ao carregar números (proxy).')
  }
  if (Array.isArray(json)) return json
  if (Array.isArray(json.data)) return json.data
  return []
}

async function fetchViaToken(areaLocal) {
  const token = (import.meta.env.VITE_BRDID_API_TOKEN || '').trim()
  if (!token) return null

  const path = (import.meta.env.VITE_BRDID_LIST_DIDS_PATH || DEFAULT_LIST_PATH).trim().replace(/^\//, '')
  const url = new URL(`${BRDID_BASE}/${path}`)
  url.searchParams.set('TOKEN', token)
  url.searchParams.set('AREA_LOCAL', String(areaLocal || '31000').trim())

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  const raw = await res.text()
  return parseDidsPayload(raw, res)
}

/**
 * @param {string} areaLocal — ex.: 31000
 * @returns {Promise<Array>}
 */
export async function fetchAvailableDids(areaLocal) {
  const serverUrl = (import.meta.env.VITE_SERVER_URL || '').trim()
  if (serverUrl) {
    return fetchViaServerProxy(areaLocal)
  }
  const viaToken = await fetchViaToken(areaLocal)
  if (viaToken !== null) {
    return viaToken
  }
  throw new Error(
    'Configure VITE_SERVER_URL (proxy) ou VITE_BRDID_API_TOKEN para listar DIDs disponíveis.'
  )
}

async function fetchLocalidadesViaServerProxy() {
  const serverUrl = (import.meta.env.VITE_SERVER_URL || '').trim().replace(/\/$/, '')
  if (!serverUrl) return null
  const url = `${serverUrl}/api/brdid/localidades`
  const res = await fetch(url)
  const raw = await res.text()
  return parseDidsPayload(raw, res)
}

async function fetchLocalidadesViaToken() {
  const token = (import.meta.env.VITE_BRDID_API_TOKEN || '').trim()
  if (!token) return null

  const path = (import.meta.env.VITE_BRDID_LOCALIDADES_PATH || DEFAULT_LOCALIDADES_PATH)
    .trim()
    .replace(/^\//, '')
  const url = new URL(`${BRDID_BASE}/${path}`)
  url.searchParams.set('TOKEN', token)

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  const raw = await res.text()
  return parseDidsPayload(raw, res)
}

/**
 * GET /buscar_localidades — localidades com DIDs disponíveis.
 * @returns {Promise<Array>}
 */
export async function fetchLocalidades() {
  const serverUrl = (import.meta.env.VITE_SERVER_URL || '').trim()
  if (serverUrl) {
    return fetchLocalidadesViaServerProxy()
  }
  const viaToken = await fetchLocalidadesViaToken()
  if (viaToken !== null) {
    return viaToken
  }
  throw new Error(
    'Configure VITE_SERVER_URL (proxy) ou VITE_BRDID_API_TOKEN para listar localidades.'
  )
}
